import type { DataType, Definition, FieldDefinition, TypeUi } from '../../models/company-data.js';

export type FieldKind = 'text' | 'number' | 'yesNo' | 'date' | 'choice' | 'reference';

export interface DataField {
  key: string;
  label: string;
  help: string;
  kind: FieldKind;
  required: boolean;
  choices: string[];
  refTypeId: string | null;
  isTitle: boolean;
  isPrivate: boolean;
}

// A type's versions are 1.<minor>.0 and only ever add, so the highest minor is the one to read and write with
export function latestVersion<T extends Pick<DataType, 'version'>>(versions: T[]): T | undefined {
  const minor = (version: string) => Number(version.split('.')[1] ?? 0);
  return [...versions].sort((a, b) => minor(b.version) - minor(a.version))[0];
}

export function parseDefinition(type: Pick<DataType, 'definition'>): Definition {
  return (typeof type.definition === 'string' ? JSON.parse(type.definition) : type.definition) as Definition;
}

export function fieldKind(field: FieldDefinition): FieldKind {
  switch (field.__type) {
    case 'string':
      return field.__enum ? 'choice' : 'text';
    case 'number':
      return 'number';
    case 'boolean':
      return 'yesNo';
    case 'date':
      return 'date';
    case 'object':
      return 'reference';
  }
}

export function labelFromKey(key: string): string {
  const words = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// The title field first, then the ui order, then fields with no order as the definition lists them
export function dataFields(definition: Definition, ui: TypeUi, privateFieldKeys: string[] = []): DataField[] {
  const keys = Object.keys(definition);
  const orderOf = (key: string) => ui.fields?.[key]?.order ?? Number.POSITIVE_INFINITY;
  const sorted = [...keys].sort((a, b) => {
    if (a === ui.titleField) return -1;
    if (b === ui.titleField) return 1;
    return orderOf(a) - orderOf(b) || keys.indexOf(a) - keys.indexOf(b);
  });

  return sorted.map((key) => {
    const field = definition[key];
    return {
      key,
      label: ui.fields?.[key]?.label || labelFromKey(key),
      help: ui.fields?.[key]?.help ?? '',
      kind: fieldKind(field),
      required: Boolean(field.__required),
      choices: field.__enum ?? [],
      refTypeId: field.__ref ?? null,
      isTitle: key === ui.titleField,
      isPrivate: privateFieldKeys.includes(key),
    };
  });
}
